import React, { useEffect, useState } from 'react';
import { callApi } from '../../api/call';
import './AuthorizeConnectCard.css';

const HELP_TEXT =
  'Enter your Authorize.Net API credentials to accept card payments for online bookings, deposits, and pre-payments.';

const EMPTY_FORM = {
  apiLoginId: '',
  transactionKey: '',
  clientKey: '',
  environment: 'PRODUCTION',
};

const AuthorizeConnectCard = ({ companyId, isSpecialAdmin }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [status, setStatus] = useState({
    connected: false,
    environment: 'PRODUCTION',
    clientKeyPresent: false,
  });
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState('idle');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  useEffect(() => {
    if (!companyId) return undefined;
    callApi(`/api/companies/${encodeURIComponent(companyId)}/payments/authorize/get`, { companyId })
      .then((res) => {
        setStatus(res.data);
        setForm((prev) => ({
          ...prev,
          environment: res.data?.environment || 'PRODUCTION',
        }));
      })
      .catch(() => {});
    return undefined;
  }, [companyId]);

  useEffect(() => {
    if (!showConfirmDialog) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setShowConfirmDialog(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showConfirmDialog]);

  const isConnected = status.connected;
  const statusLabel = isConnected
    ? `Connected${isSpecialAdmin && status.environment ? ` · ${status.environment}` : ''}`
    : 'Not connected';

  const handleSave = async (e) => {
    e?.preventDefault();
    if (!companyId) return;
    setLoading(true);
    setAction('saving');
    setError('');
    setSuccess('');
    try {
      const { apiLoginId, transactionKey, clientKey, environment } = form;
      await callApi(`/api/companies/${encodeURIComponent(companyId)}/payments/authorize`, {
        companyId,
        apiLoginId,
        transactionKey,
        clientKey,
        ...(isSpecialAdmin ? { environment } : {}),
      });
      setSuccess('Authorize.Net settings saved.');
      setStatus({
        connected: true,
        environment: form.environment,
        clientKeyPresent: !!form.clientKey,
      });
    } catch (err) {
      setError(err?.message || 'Failed to save settings');
    } finally {
      setLoading(false);
      setAction('idle');
    }
  };

  const handleTest = async () => {
    setLoading(true);
    setAction('testing');
    setError('');
    setSuccess('');
    try {
      await callApi(`/api/companies/${encodeURIComponent(companyId)}/payments/authorize/test`, {});
      setSuccess('Credentials verified successfully.');
    } catch (err) {
      setError(err?.message || 'Authorize.Net test failed');
    } finally {
      setLoading(false);
      setAction('idle');
    }
  };

  const runDisconnect = async () => {
    if (!companyId) return;
    setError('');
    setSuccess('');
    setStatus({
      connected: false,
      environment: status.environment || 'PRODUCTION',
      clientKeyPresent: false,
    });
    setForm({
      ...EMPTY_FORM,
      environment: form.environment || status.environment || 'PRODUCTION',
    });

    try {
      await callApi(`/api/companies/${encodeURIComponent(companyId)}/payments/authorize/disconnect`, {
        companyId,
      });
      setSuccess('Authorize.Net disconnected.');
    } catch (err) {
      setError(err?.message || 'Failed to disconnect');
    }
  };

  const handleDisconnectClick = () => {
    setShowConfirmDialog(true);
  };

  const handleDisconnectConfirm = async () => {
    setShowConfirmDialog(false);
    setLoading(true);
    setAction('disconnecting');
    await runDisconnect();
    setLoading(false);
    setAction('idle');
  };

  return (
    <div className="authorize-connect">
      <article className="an-card" aria-labelledby="authorize-connect-title">
        <header className="an-card__header">
          <div className="an-card__brand">
            <img
              src="/authorizenet.jpg"
              alt=""
              className="an-card__logo"
              aria-hidden="true"
            />
            <div>
              <h2 id="authorize-connect-title" className="an-card__title">
                Authorize.Net
                <button
                  type="button"
                  className="an-help"
                  title={HELP_TEXT}
                  aria-label={HELP_TEXT}
                >
                  ?
                </button>
              </h2>
              <p className="an-card__subtitle">API credentials for card payments</p>
            </div>
          </div>
          <span
            className={`an-status an-status--${isConnected ? 'connected' : 'disconnected'}`}
            role="status"
          >
            <span className="an-status__dot" aria-hidden="true" />
            {statusLabel}
          </span>
        </header>

        <div className="an-card__body">
          {isConnected ? (
            <div className="an-connected-note" role="status">
              <div>
                <strong>Ready to accept payments</strong>
                Your Authorize.Net credentials are saved. Use Test to verify they still work,
                or update the fields below and Save.
              </div>
            </div>
          ) : (
            <>
              <p className="an-description">
                Add your API Login ID and Transaction Key from the Authorize.Net merchant
                portal to enable online payments.
              </p>
              <ul className="an-features">
                <li>Online booking and deposit payments</li>
                <li>Test credentials before accepting live payments</li>
                <li>Optional Client Key for Accept.js hosted fields</li>
              </ul>
            </>
          )}

          {error && (
            <div className="an-alert an-alert--error" role="alert">
              {error}
            </div>
          )}
          {success && (
            <div className="an-alert an-alert--success" role="status">
              {success}
            </div>
          )}

          <form className="an-form-grid" onSubmit={handleSave}>
            <div className="an-field">
              <label htmlFor="apiLoginId" className="an-label">
                API Login ID
              </label>
              <input
                id="apiLoginId"
                className="an-input"
                type="text"
                value={form.apiLoginId}
                onChange={(e) => setForm({ ...form, apiLoginId: e.target.value })}
                placeholder="e.g., 7T8s9K..."
                autoComplete="off"
              />
            </div>

            <div className="an-field">
              <label htmlFor="transactionKey" className="an-label">
                Transaction Key
              </label>
              <input
                id="transactionKey"
                className="an-input"
                type="password"
                value={form.transactionKey}
                onChange={(e) => setForm({ ...form, transactionKey: e.target.value })}
                placeholder="Enter your transaction key"
                autoComplete="new-password"
              />
            </div>

            <div className="an-field an-field--full">
              <label htmlFor="clientKey" className="an-label">
                Client Key <span className="an-label-hint">(optional)</span>
              </label>
              <input
                id="clientKey"
                className="an-input"
                type="password"
                value={form.clientKey}
                onChange={(e) => setForm({ ...form, clientKey: e.target.value })}
                placeholder="For Accept.js hosted payment fields"
                autoComplete="new-password"
              />
            </div>

            {isSpecialAdmin && (
              <div className="an-field">
                <label htmlFor="environment" className="an-label">
                  Environment
                </label>
                <select
                  id="environment"
                  className="an-select"
                  value={form.environment}
                  onChange={(e) => setForm({ ...form, environment: e.target.value })}
                >
                  <option value="PRODUCTION">Production</option>
                  <option value="SANDBOX">Sandbox</option>
                </select>
              </div>
            )}
          </form>

          <div className="an-actions">
            <button
              type="button"
              className="an-btn an-btn--primary"
              onClick={handleSave}
              disabled={loading}
            >
              {action === 'saving' ? (
                <>
                  <span className="an-spinner" aria-hidden="true" />
                  Saving…
                </>
              ) : (
                'Save credentials'
              )}
            </button>
            <button
              type="button"
              className="an-btn an-btn--secondary"
              onClick={handleTest}
              disabled={loading || !isConnected}
            >
              {action === 'testing' ? (
                <>
                  <span className="an-spinner" aria-hidden="true" />
                  Testing…
                </>
              ) : (
                'Test connection'
              )}
            </button>
            {isConnected && (
              <button
                type="button"
                className="an-btn an-btn--danger"
                onClick={handleDisconnectClick}
                disabled={loading}
              >
                Disconnect
              </button>
            )}
            {status.clientKeyPresent && (
              <span className="an-inline-note">Client key on file</span>
            )}
          </div>

          <p className="an-help-text">
            Find credentials in Authorize.Net: <strong>Account → API Credentials &amp; Keys</strong>.
          </p>
        </div>
      </article>

      {showConfirmDialog && (
        <div className="an-modal-root" role="presentation">
          <button
            type="button"
            className="an-modal-backdrop"
            aria-label="Close dialog"
            onClick={() => setShowConfirmDialog(false)}
          />
          <div
            className="an-modal-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="authorize-disconnect-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="authorize-disconnect-title" className="an-modal-title">
              Disconnect Authorize.Net?
            </h2>
            <p className="an-modal-body">
              Saved credentials will be removed from Neat Schedule. Your Authorize.Net account is
              not deleted—you can enter credentials again anytime.
            </p>
            <div className="an-modal-footer">
              <button
                type="button"
                className="an-btn an-btn--ghost"
                onClick={() => setShowConfirmDialog(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="an-btn an-btn--danger"
                onClick={handleDisconnectConfirm}
                disabled={loading}
                autoFocus
              >
                {action === 'disconnecting' ? 'Disconnecting…' : 'Disconnect'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuthorizeConnectCard;
